import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { RadioOrSelectOption, TrackedSession } from '../../../../types/common.types';
import {
  AccountabilityTemplate,
  Amount,
  AttendanceSession,
  AttendanceSessionTemplate,
  CharityActivity,
  Collection,
  Condition,
  Measurement,
  MeasurementDiagram,
  MemberLeaderboard as MemberLeaderboardType,
  OptionalExercise,
  ParentProgrammeGeneralDetails,
  Programme,
  RecommendedExercise,
  Resource,
  SelfCareActivity,
  SubProgrammeGeneralDetails,
  SurveyValueExcelFailure,
  TeamLeaderboard as TeamLeaderboardType,
  Weighting
} from '../../../../types/entity.types';

import { ACTIVE, ARCHIVED, MEMBERS, TEAMS, UPCOMING } from '../../../../utils/constants';
import { checkForAccountabilityChanges } from '../../../../utils/diff.util';
import { getClassNames } from '../../../../utils/util';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import Dropdown from '../../../common/input/dropdown/Dropdown';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';
import AccountabilityCharity from './accountability-charity/AccountabilityCharity';
import AccountabilityCondition from './accountability-condition/AccountabilityCondition';
import AccountabilityMeasurements from './accountability-measurements/AccountabilityMeasurements';
import AccountabilityMovement from './accountability-movement/AccountabilityMovement';
import AccountabilityNutrition from './accountability-nutrition/AccountabilityNutrition';
import AccountabilitySelfCare from './accountability-self-care/AccountabilitySelfCare';
import AccountabilitySessions from './accountability-sessions/AccountabilitySessions';
import AccountabilitySurvey from './accountability-survey/AccountabilitySurvey';
import AccountabilityWeightings from './accountability-weightings/AccountabilityWeightings';
import MemberLeaderboard from './leaderboard/MemberLeaderboard';
import TeamLeaderboard from './leaderboard/TeamLeaderboard';

import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';
import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';

import './accountability.scss';

type Props = {
  status: string;
  programme?: Programme;
  parentProgramme?: ParentProgrammeGeneralDetails;
  subProgramme?: SubProgrammeGeneralDetails;
  subProgrammeOption?: RadioOrSelectOption;
  leaderboard?: MemberLeaderboardType;
  teamLeaderboard?: TeamLeaderboardType;
  accountabilityTemplate?: AccountabilityTemplate;
  collections?: Collection[];
  weightings: Weighting;
  measurement: Measurement;
  mealPlans?: Resource[];
  conditions?: Condition[];
  recommendedExercises?: RecommendedExercise[];
  optionalExercises?: OptionalExercise[];
  charityActivities?: CharityActivity[];
  selfCareActivities?: SelfCareActivity[];
  attendanceSessions: AttendanceSession[];
  trackedSessions?: TrackedSession[];
  updateWeighting: (key: keyof Weighting, value: number) => void;
  uploadSurvey: (file: File, onError: (errors: SurveyValueExcelFailure[]) => void) => void;
  updateMeasurement: (key: keyof Measurement, value: MeasurementDiagram | string | undefined) => void;
  createMealPlan: (collectionExternalReference: string, mealPlan: Resource) => void;
  updateMealPlan: (resource: Resource) => void;
  deleteMealPlan: (collectionExternalReference: string, uuid: string) => void;
  createCondition: () => void;
  updateCondition: (externalReference: string, key: keyof Condition, value: string) => void;
  deleteCondition: (externalReference: string) => void;
  createRecommendedExercise: () => void;
  updateRecommendedExercise: (
    externalReference: string,
    key: keyof Omit<RecommendedExercise, 'amounts'>,
    value: string
  ) => void;
  deleteRecommendedExercise: (externalReference: string) => void;
  createExerciseAmount: (exerciseExternalReference: string) => void;
  updateExerciseAmount: (
    exerciseExternalReference: string,
    amountExternalReference: string,
    key: keyof Amount,
    value: string
  ) => void;
  deleteExerciseAmount: (exerciseExternalReference: string, amountExternalReference: string) => void;
  createOptionalExercise: () => void;
  updateOptionalExercise: (externalReference: string, key: keyof OptionalExercise, value: string) => void;
  deleteOptionalExercise: (externalReference: string) => void;
  createCharityActivity: () => void;
  updateCharityActivity: (
    externalReference: string,
    key: keyof Omit<CharityActivity, 'amounts'>,
    value: string
  ) => void;
  deleteCharityActivity: (externalReference: string) => void;
  createCharityAmount: (activityExternalReference: string) => void;
  updateCharityAmount: (
    activityExternalReference: string,
    amountExternalReference: string,
    key: keyof Amount,
    value: string
  ) => void;
  deleteCharityAmount: (activityExternalReference: string, amountExternalReference: string) => void;
  createSelfCareActivity: () => void;
  updateSelfCareActivity: (externalReference: string, value: string) => void;
  deleteSelfCareActivity: (externalReference: string) => void;
  updateAttendanceSession: (session: Partial<AttendanceSession>) => void;
  deleteAttendanceSession: (externalReference: string) => void;
  createTrackedSession: (session: TrackedSession) => void;
  updateTrackedSession: (session: TrackedSession) => void;
  deleteTrackedSession: (uuid: string) => void;
  saveAccountabilityTemplate: (onSuccess: () => void) => void;
  generateAttendanceSessions: (sessionTemplate: AttendanceSessionTemplate, onSuccess: () => void) => void;
  downloadLeaderboardExcel: () => void;
  downloadDocument: (resource: Resource) => void;
  downloadTeamLeaderboardExcel: () => void;
  onSubProgrammeChange: (subProgramme: RadioOrSelectOption) => void;
  loadAccountability: (externalReference: string) => void;
  downloadTrackedSessionDashboardExcel: (sessionEventExternalReference: string) => void;
};

const Accountability = ({
  status,
  programme,
  parentProgramme,
  subProgramme,
  subProgrammeOption,
  leaderboard,
  teamLeaderboard,
  accountabilityTemplate,
  collections = [],
  weightings,
  measurement,
  mealPlans = [],
  conditions = [],
  recommendedExercises = [],
  optionalExercises = [],
  charityActivities = [],
  selfCareActivities = [],
  attendanceSessions = [],
  trackedSessions = [],
  updateWeighting,
  uploadSurvey,
  updateMeasurement,
  createMealPlan,
  updateMealPlan,
  deleteMealPlan,
  createCondition,
  updateCondition,
  deleteCondition,
  createRecommendedExercise,
  updateRecommendedExercise,
  deleteRecommendedExercise,
  createExerciseAmount,
  updateExerciseAmount,
  deleteExerciseAmount,
  createOptionalExercise,
  updateOptionalExercise,
  deleteOptionalExercise,
  createCharityActivity,
  updateCharityActivity,
  deleteCharityActivity,
  createCharityAmount,
  updateCharityAmount,
  deleteCharityAmount,
  createSelfCareActivity,
  updateSelfCareActivity,
  deleteSelfCareActivity,
  updateAttendanceSession,
  deleteAttendanceSession,
  createTrackedSession,
  updateTrackedSession,
  deleteTrackedSession,
  saveAccountabilityTemplate,
  generateAttendanceSessions,
  downloadLeaderboardExcel,
  downloadDocument,
  downloadTeamLeaderboardExcel,
  onSubProgrammeChange,
  loadAccountability,
  downloadTrackedSessionDashboardExcel
}: Props) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>();
  const [unsavedSubProgramme, setUnsavedSubProgramme] = useState<string>();

  const navigate = useNavigate();

  const { programmeExternalReference, view } = useParams();

  const [searchParams] = useSearchParams();

  const subProgrammeOptions = useMemo(() => {
    if (!parentProgramme?.subProgrammeSummaries) return [];

    const options = parentProgramme.subProgrammeSummaries.map((subProgramme) => ({
      label: subProgramme.name,
      value: subProgramme.externalReference
    }));

    return options;
  }, [parentProgramme]);

  const checkForUnsavedChanges = (url?: string, subProgrammeExternalReference?: string) => {
    const hasUnsavedChanges = checkForAccountabilityChanges(
      accountabilityTemplate,
      weightings,
      measurement,
      conditions,
      recommendedExercises,
      optionalExercises,
      selfCareActivities,
      attendanceSessions,
      trackedSessions,
      charityActivities
    );

    if (url) {
      if (hasUnsavedChanges) {
        setRedirectUrl(url);
      } else {
        navigate(url);
      }
    } else if (subProgrammeExternalReference) {
      if (hasUnsavedChanges) {
        setUnsavedSubProgramme(subProgrammeExternalReference);
      } else {
        onSubProgrammeChange(subProgrammeOptions.find((option) => option.value === subProgrammeExternalReference)!);
      }
    }
  };

  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  const handleSaveClick = () => {
    saveAccountabilityTemplate(() => {
      if (redirectUrl) {
        navigate(redirectUrl);
        setRedirectUrl(undefined);
      } else if (unsavedSubProgramme) {
        const option = subProgrammeOptions.find((option) => option.value === unsavedSubProgramme)!;
        onSubProgrammeChange(option);
        setUnsavedSubProgramme(undefined);
      } else {
        if (subProgrammeOption?.value) {
          loadAccountability(subProgrammeOption.value);
        } else {
          loadAccountability(programmeExternalReference!);
        }
      }
    });
  };

  const handleDiscardUnsavedChangesClick = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
      setRedirectUrl(undefined);
    } else if (unsavedSubProgramme) {
      const option = subProgrammeOptions.find((option) => option.value === unsavedSubProgramme)!;
      onSubProgrammeChange(option);
      setUnsavedSubProgramme(undefined);
    }
  };

  const handleSubProgrammeChange = (option: RadioOrSelectOption) => {
    checkForUnsavedChanges(undefined, option.value);
  };

  const handleToggleClick = (newView: string) => {
    if (newView === view) return;

    navigate(
      `/programmes/${status}/${programmeExternalReference}/accountability/${newView}?hasSubProgrammes=${hasSubProgrammes}`
    );
  };

  const chariryWorkEnabled = !!(subProgramme?.charityWorkEnabled ?? programme?.charityWork);

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="accountability__icons">
        {((settingsOpen && status !== ARCHIVED) || status === UPCOMING) && (
          <div className="accountability__icon-container" onClick={handleSaveClick}>
            <SaveIcon className="accountability__save-icon" data-testid="save-icon" />
          </div>
        )}
        {!settingsOpen && status !== UPCOMING && (
          <div className="accountability__icon-container" onClick={() => setSettingsOpen(true)}>
            <GearIcon className="accountability__gear-icon" data-testid="cog-icon" />
          </div>
        )}
        {settingsOpen && status !== UPCOMING && (
          <div className="accountability__icon-container" onClick={() => setSettingsOpen(false)}>
            <CloseIcon className="accountability__close-icon" data-testid="times-icon" />
          </div>
        )}
      </div>

      {!settingsOpen && (status === ACTIVE || status === ARCHIVED) && (
        <div className="accountability" data-testid={`accountability-${status}`}>
          {hasSubProgrammes && (
            <div className="accountability__sub-programme">
              <Dropdown
                name="subProgramme"
                value={subProgrammeOption}
                options={subProgrammeOptions}
                onChange={handleSubProgrammeChange}
              />
            </div>
          )}

          <div className="accountability__leaderboard-header">
            <h1 className="">Leaderboard</h1>
            <div className="accountability__toggle">
              <button
                className={getClassNames('accountability__toggle-button', { active: view === MEMBERS })}
                onClick={() => handleToggleClick(MEMBERS)}
              >
                Members
              </button>
              <button
                className={getClassNames('accountability__toggle-button', { active: view === TEAMS })}
                onClick={() => handleToggleClick(TEAMS)}
              >
                Teams
              </button>
            </div>
          </div>

          {view === TEAMS ? (
            <TeamLeaderboard leaderboard={teamLeaderboard} downloadExcel={downloadTeamLeaderboardExcel} />
          ) : (
            <MemberLeaderboard leaderboard={leaderboard} downloadExcel={downloadLeaderboardExcel} />
          )}
        </div>
      )}

      {(settingsOpen || status === UPCOMING) && (
        <div className="accountability" data-testid={`accountability-${status}`}>
          <div
            className={getClassNames('accountability__settings-header', {
              short: status !== ACTIVE
            })}
          >
            {status === ACTIVE && (
              <p className="accountability__warning">
                PLEASE NOTE: Editing an active programme should be limited to correcting spelling or numeric mistakes
              </p>
            )}
          </div>
          <div className="accountability__settings-list">
            {hasSubProgrammes && (
              <div
                className={getClassNames('accountability__sub-programme', {
                  active: status === ACTIVE,
                  settings: true
                })}
              >
                <Dropdown
                  name="subProgramme"
                  value={subProgrammeOption}
                  options={subProgrammeOptions}
                  onChange={handleSubProgrammeChange}
                />
              </div>
            )}

            <AccountabilityWeightings
              charityWork={chariryWorkEnabled}
              status={status}
              weightings={weightings}
              updateWeighting={updateWeighting}
            />
            <hr className="accountability__divider" />
            <AccountabilitySurvey status={status} uploadSurvey={uploadSurvey} />
            <hr className="accountability__divider" />
            <AccountabilityMeasurements
              status={status}
              measurement={measurement}
              updateMeasurement={updateMeasurement}
            />
            <hr className="accountability__divider" />
            <AccountabilityNutrition
              status={status}
              collections={collections}
              mealPlans={mealPlans}
              createMealPlan={createMealPlan}
              updateMealPlan={updateMealPlan}
              deleteMealPlan={deleteMealPlan}
              downloadDocument={downloadDocument}
            />
            <hr className="accountability__divider" />
            <AccountabilityCondition
              status={status}
              conditions={conditions}
              createCondition={createCondition}
              updateCondition={updateCondition}
              deleteCondition={deleteCondition}
            />
            <hr className="accountability__divider" />
            <AccountabilityMovement
              status={status}
              programme={programme}
              parentProgramme={parentProgramme}
              recommendedExercises={recommendedExercises}
              optionalExercises={optionalExercises}
              createRecommendedExercise={createRecommendedExercise}
              updateRecommendedExercise={updateRecommendedExercise}
              deleteRecommendedExercise={deleteRecommendedExercise}
              createExerciseAmount={createExerciseAmount}
              updateExerciseAmount={updateExerciseAmount}
              deleteExerciseAmount={deleteExerciseAmount}
              createOptionalExercise={createOptionalExercise}
              updateOptionalExercise={updateOptionalExercise}
              deleteOptionalExercise={deleteOptionalExercise}
            />
            {chariryWorkEnabled && (
              <>
                <hr className="accountability__divider" />
                <AccountabilityCharity
                  status={status}
                  programme={programme}
                  parentProgramme={parentProgramme}
                  charityActivities={charityActivities}
                  createCharityActivity={createCharityActivity}
                  updateCharityActivity={updateCharityActivity}
                  deleteCharityActivity={deleteCharityActivity}
                  createCharityAmount={createCharityAmount}
                  updateCharityAmount={updateCharityAmount}
                  deleteCharityAmount={deleteCharityAmount}
                />
              </>
            )}
            <hr className="accountability__divider" />
            <AccountabilitySelfCare
              status={status}
              selfCareActivities={selfCareActivities}
              createSelfCareActivity={createSelfCareActivity}
              updateSelfCareActivity={updateSelfCareActivity}
              deleteSelfCareActivity={deleteSelfCareActivity}
            />
            <hr className="accountability__divider" />
            <AccountabilitySessions
              status={status}
              attendanceSessions={attendanceSessions}
              trackedSessions={trackedSessions}
              updateAttendanceSession={updateAttendanceSession}
              deleteAttendanceSession={deleteAttendanceSession}
              generateAttendanceSessions={generateAttendanceSessions}
              createTrackedSession={createTrackedSession}
              updateTrackedSession={updateTrackedSession}
              deleteTrackedSession={deleteTrackedSession}
              downloadTrackedSessionDashboardExcel={downloadTrackedSessionDashboardExcel}
            />
          </div>
        </div>
      )}

      <SaveChangesModal
        show={!!(unsavedSubProgramme || redirectUrl)}
        redirectUrl={redirectUrl}
        onSaveClick={handleSaveClick}
        onDiscardClick={handleDiscardUnsavedChangesClick}
      />
    </ProgrammeLayout>
  );
};

export default Accountability;
